import React, { useState, useEffect } from 'react';
import Select from 'react-select';
// Admin.styles.css is still imported but most styles will be overridden by Tailwind classes
import '../../pages/Admin/Admin.styles.css';
import { API_config } from '../../config/apiConfig';

/**
 * Format local datetime string (YYYY-MM-DDTHH:MM) to API format (YYYY-MM-DD HH:MM:SS)
 * @param {string} dateString 
 * @returns {string}
 */
const formatForApi = (dateString) => {
    if (!dateString) return '';
    return dateString.replace('T', ' ') + ':00';
};

/**
 * Get today's date in YYYY-MM-DDTHH:MM format
 * @param {boolean} endOfDay 
 * @returns {string}
 */
const getDefaultDate = (endOfDay = false) => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const time = endOfDay ? '23:59' : '00:00';
    return `${year}-${month}-${day}T${time}`;
};

const RecceReport = () => {
    // State
    const [geofences, setGeofences] = useState([]);
    const [selectedGeofences, setSelectedGeofences] = useState([]);
    const [fromDate, setFromDate] = useState(getDefaultDate(false));
    const [toDate, setToDate] = useState(getDefaultDate(true));
    const [searchQuery, setSearchQuery] = useState('');
    const [highlightThreshold, setHighlightThreshold] = useState(3);

    // Loading & Error States
    const [loadingGeofences, setLoadingGeofences] = useState(false);
    const [loadingReport, setLoadingReport] = useState(false);
    const [error, setError] = useState(null);
    const [reportData, setReportData] = useState(null);
    const [expandedRows, setExpandedRows] = useState({}); // Track expanded device rows

    // Fetch Geofences on Mount
    useEffect(() => {
        const fetchGeofences = async () => {
            setLoadingGeofences(true);
            setError(null);
            try {
                const token = API_config.bearerToken;
                const response = await fetch(`${API_config.recceApiUrl}?type=geofences`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });

                if (response.status === 401) throw new Error('Unauthorized');
                if (!response.ok) throw new Error(`API Error: ${response.status}`);

                const data = await response.json();
                if (data.success && Array.isArray(data.items)) {
                    setGeofences(data.items.map(item => ({
                        value: item.id,
                        label: item.name
                    })));
                }
            } catch (err) {
                console.error('Error fetching geofences:', err);
                setError(err.message);
            } finally {
                setLoadingGeofences(false);
            }
        };
        fetchGeofences();
    }, []);

    // Helper to process raw events into pivot data
    const processEvents = (data) => {
        const events = data.events || [];
        const devices = {};

        // Group events by device
        events.forEach(event => {
            if (!devices[event.deviceId]) {
                devices[event.deviceId] = {
                    deviceId: event.deviceId,
                    deviceName: event.deviceName,
                    events: [],
                    counts: {}
                };
            }

            // Add to device event list
            devices[event.deviceId].events.push(event);

            // Count enters per geofence for the cell value
            if (event.eventType === 'geofenceEnter') {
                const geoId = event.geofenceId;
                devices[event.deviceId].counts[geoId] = (devices[event.deviceId].counts[geoId] || 0) + 1;
            }
        });

        // Convert grouped object to array
        return Object.values(devices);
    };

    const handleGenerateReport = async () => {
        if (selectedGeofences.length === 0) {
            setError('Please select at least one geofence');
            return;
        }
        if (fromDate > toDate) {
            setError('"From" date cannot be after "To" date');
            return;
        }

        setLoadingReport(true);
        setError(null);
        setReportData(null);
        setExpandedRows({});

        try {
            const token = API_config.bearerToken;
            const fromFormatted = formatForApi(fromDate);
            const toFormatted = formatForApi(toDate);
            const geofenceIds = selectedGeofences.map(g => g.value).join(',');

            // Use standard events endpoint
            const url = `${API_config.recceApiUrl}?type=geofence_events&geofenceId=${geofenceIds}&from=${encodeURIComponent(fromFormatted)}&to=${encodeURIComponent(toFormatted)}`;

            console.log('Fetching report from:', url);

            const response = await fetch(url, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (!response.ok) throw new Error(`API Error: ${response.status}`);

            const contentType = response.headers.get("content-type");
            if (!contentType || !contentType.includes("application/json")) {
                const text = await response.text();
                console.error('API Error: Non-JSON response received');

                if (text.trim().startsWith('<!DOCTYPE')) {
                    throw new Error('Server returned HTML instead of JSON (likely 404/500). Check API URL.');
                }
                throw new Error('Received non-JSON response');
            }

            const data = await response.json();

            if (data.success) {
                const processedRows = processEvents(data);

                // Sort rows by name
                processedRows.sort((a, b) => (a.deviceName || '').localeCompare(b.deviceName || ''));

                setReportData({
                    ...data,
                    rows: processedRows
                });
            } else {
                throw new Error(data.message || 'Failed to fetch report');
            }
        } catch (err) {
            console.error('Error fetching report:', err);
            setError(err.message);
        } finally {
            setLoadingReport(false);
        }
    };

    const toggleRow = (deviceId) => {
        setExpandedRows(prev => ({
            ...prev,
            [deviceId]: !prev[deviceId]
        }));
    };

    const toggleAll = () => {
        if (!reportData || !reportData.rows) return;

        const allExpanded = reportData.rows.every(row => expandedRows[row.deviceId]);

        if (allExpanded) {
            setExpandedRows({});
        } else {
            const newExpanded = {};
            reportData.rows.forEach(row => {
                newExpanded[row.deviceId] = true;
            });
            setExpandedRows(newExpanded);
        }
    };

    const getTotalPasses = (row) => {
        let total = 0;
        selectedGeofences.forEach(g => {
            total += (row.counts[g.value] || 0);
        });
        return total;
    };

    const exportCSV = () => {
        if (!reportData || !reportData.rows) return;

        // Define headers
        let csvContent = "Device ID,Device Name";
        selectedGeofences.forEach(g => {
            csvContent += `,${g.label} (Enter)`;
        });
        csvContent += ",Total Passes\n";

        // Add rows
        reportData.rows.forEach(row => {
            let rowStr = `${row.deviceId},"${row.deviceName || ''}"`;
            let total = 0;
            selectedGeofences.forEach(g => {
                const count = row.counts[g.value] || 0;
                total += count;
                rowStr += `,${count}`;
            });
            rowStr += `,${total}`;
            csvContent += rowStr + "\n";
        });

        // Trigger download
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute("download", `recce_report_${new Date().toISOString().slice(0, 10)}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Filter rows based on search
    const filteredRows = reportData?.rows?.filter(row =>
        (row.deviceName || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        String(row.deviceId).includes(searchQuery)
    ) || [];

    // Custom CSS Grid style for dynamic columns
    // 3fr for name, 1fr for each geofence, 1fr for total
    const gridStyle = {
        gridTemplateColumns: `minmax(250px, 3fr) repeat(${selectedGeofences.length}, minmax(120px, 1fr)) minmax(120px, 1fr)`
    };

    // Custom Styles for React Select - Updated for Dark Mode
    const customStyles = {
        control: (base, state) => ({
            ...base,
            borderColor: 'var(--border-color)',
            backgroundColor: 'var(--bg-tertiary)',
            color: 'var(--text-primary)',
            minHeight: '44px',
            borderRadius: '8px',
            boxShadow: 'none',
            padding: '2px 8px',
            '&:hover': {
                borderColor: 'var(--primary)'
            }
        }),
        menu: (base) => ({
            ...base,
            zIndex: 9999,
            backgroundColor: 'var(--bg-secondary)',
            borderColor: 'var(--border-color)',
            color: 'var(--text-primary)'
        }),
        option: (base, state) => ({
            ...base,
            backgroundColor: state.isFocused ? 'var(--hover-bg)' : 'transparent',
            color: 'var(--text-primary)',
            cursor: 'pointer'
        }),
        singleValue: (base) => ({
            ...base,
            color: 'var(--text-primary)'
        }),
        input: (base) => ({
            ...base,
            color: 'var(--text-primary)'
        }),
        multiValue: (base) => ({
            ...base,
            backgroundColor: 'var(--active-bg)',
            borderRadius: '6px',
            border: '1px solid var(--primary)',
        }),
        multiValueLabel: (base) => ({
            ...base,
            color: 'var(--primary)',
            fontWeight: 600,
        }),
        multiValueRemove: (base) => ({
            ...base,
            color: 'var(--primary)',
            ':hover': {
                backgroundColor: 'var(--primary)',
                color: 'white',
            },
        }),
    };

    return (
        <div className="min-h-screen p-6 max-w-[1400px] mx-auto text-[var(--text-primary)] font-sans">
            {/* Header */}
            <header className="mb-6 flex justify-between items-center">
                <div>
                    <h1 className="text-2xl font-bold text-[var(--text-primary)]">Recce Geofence Report</h1>
                    <p className="text-sm text-[var(--text-secondary)]">Administrative dashboard for GPS monitoring and geofence analytics</p>
                </div>
                <div className="flex gap-2">
                    <button
                        onClick={exportCSV}
                        disabled={!reportData}
                        className="flex items-center gap-2 bg-[var(--bg-secondary)] border border-[var(--border-color)] px-4 py-2 rounded-lg text-sm font-medium hover:bg-[var(--hover-bg)] transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed text-[var(--text-primary)]"
                    >
                        <span className="material-icons-outlined text-[20px]">download</span>
                        Export CSV
                    </button>
                </div>
            </header>

            {/* Error Message */}
            {error && (
                <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg relative dark:bg-red-900/20 dark:border-red-800 dark:text-red-400" role="alert">
                    <strong className="font-bold">Error: </strong>
                    <span className="block sm:inline">{error}</span>
                </div>
            )}

            {/* Filter Card */}
            <div className="bg-[var(--bg-secondary)] rounded-xl shadow-sm border border-[var(--border-color)] p-5 mb-6 transition-colors">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
                    {/* Geofence Selection */}
                    <div className="md:col-span-3">
                        <label className="block text-xs font-semibold text-[var(--text-secondary)] uppercase tracking-wider mb-2">Geofence Selection</label>
                        <Select
                            isMulti
                            options={geofences}
                            value={selectedGeofences}
                            onChange={setSelectedGeofences}
                            isLoading={loadingGeofences}
                            isDisabled={loadingReport}
                            placeholder="Select geofences..."
                            className="react-select-container text-sm"
                            classNamePrefix="react-select"
                            menuPortalTarget={document.body}
                            styles={customStyles}
                            components={{
                                IndicatorSeparator: () => null
                            }}
                        />
                    </div>

                    {/* From Date */}
                    <div className="md:col-span-2">
                        <label className="block text-xs font-semibold text-[var(--text-secondary)] uppercase tracking-wider mb-2">From</label>
                        <div className="relative">
                            <input
                                type="datetime-local"
                                value={fromDate}
                                onChange={(e) => setFromDate(e.target.value)}
                                className="w-full border border-[var(--border-color)] rounded-lg text-sm bg-[var(--bg-tertiary)] focus:ring-blue-500 focus:border-blue-500 p-2.5 h-[42px] text-[var(--text-primary)]"
                            />
                        </div>
                    </div>

                    {/* To Date */}
                    <div className="md:col-span-2">
                        <label className="block text-xs font-semibold text-[var(--text-secondary)] uppercase tracking-wider mb-2">To</label>
                        <div className="relative">
                            <input
                                type="datetime-local"
                                value={toDate}
                                onChange={(e) => setToDate(e.target.value)}
                                className="w-full border border-[var(--border-color)] rounded-lg text-sm bg-[var(--bg-tertiary)] focus:ring-blue-500 focus:border-blue-500 p-2.5 h-[42px] text-[var(--text-primary)]"
                            />
                        </div>
                    </div>

                    {/* Threshold Input */}
                    <div className="md:col-span-1">
                        <label className="block text-xs font-semibold text-[var(--text-secondary)] uppercase tracking-wider mb-2" title="Highlight cells with value greater than this">
                            Alert &gt;
                        </label>
                        <div className="relative">
                            <input
                                type="number"
                                min="0"
                                value={highlightThreshold}
                                onChange={(e) => setHighlightThreshold(Number(e.target.value))}
                                className="w-full border border-[var(--border-color)] rounded-lg text-sm bg-[var(--bg-tertiary)] focus:ring-blue-500 focus:border-blue-500 p-2.5 h-[42px] text-center font-bold text-red-600 dark:text-red-400"
                            />
                        </div>
                    </div>

                    {/* Generate Button */}
                    <div className="md:col-span-1 flex justify-end">
                        <button
                            onClick={handleGenerateReport}
                            disabled={loadingReport || selectedGeofences.length === 0}
                            className="bg-blue-600 hover:bg-blue-700 text-white w-full h-[42px] rounded-lg font-semibold text-sm flex items-center justify-center gap-2 transition-colors shadow-sm disabled:opacity-70 disabled:cursor-not-allowed"
                            title="Search / Generate Report"
                        >
                            {loadingReport ? (
                                <span className="material-icons-outlined animate-spin text-[24px]">refresh</span>
                            ) : (
                                <span className="material-icons-outlined text-[24px]">search</span>
                            )}
                        </button>
                    </div>

                    {/* Client Search */}
                    <div className="md:col-span-2">
                        <div className="relative">
                            <input
                                className="w-full pl-8 pr-3 py-2 border border-[var(--border-color)] rounded-lg text-sm bg-[var(--bg-tertiary)] focus:ring-blue-500 focus:border-blue-500 h-[42px] text-[var(--text-primary)] placeholder-[var(--text-tertiary)]"
                                placeholder="Filter devices..."
                                type="text"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                disabled={!reportData}
                            />
                            <span className="material-icons-outlined absolute left-2 top-1/2 -translate-y-1/2 text-[var(--text-tertiary)] text-[18px]">filter_alt</span>
                        </div>
                    </div>

                    {/* Expand All Button */}
                    <div className="md:col-span-1 flex justify-end">
                        <button
                            onClick={toggleAll}
                            disabled={!reportData}
                            className="bg-[var(--bg-tertiary)] hover:bg-[var(--hover-bg)] text-[var(--text-secondary)] border border-[var(--border-color)] w-full h-[42px] rounded-lg font-semibold text-sm flex items-center justify-center transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                            title={reportData && reportData.rows && reportData.rows.length > 0 && reportData.rows.every(row => expandedRows[row.deviceId]) ? 'Collapse All' : 'Expand All'}
                        >
                            <span className="material-icons-outlined text-[24px]">unfold_more</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* Results Section */}
            {reportData && (
                <>
                    <div className="space-y-3">
                        {/* Table Header Row (Hidden on Mobile) */}
                        <div
                            className="hidden md:grid px-6 py-3 text-xs font-bold text-[var(--text-secondary)] uppercase tracking-wider items-center gap-4 border-b border-[var(--border-color)]"
                            style={gridStyle}
                        >
                            <div className="flex items-center gap-1">DEVICE / DRIVER</div>
                            {selectedGeofences.map(geofence => (
                                <div key={geofence.value} className="text-center flex items-center justify-center gap-1">
                                    {geofence.label}
                                </div>
                            ))}
                            <div className="text-right">TOTAL PASSES</div>
                        </div>

                        {/* Rows */}
                        {filteredRows.length > 0 ? (
                            filteredRows.map((row) => {
                                const totalPasses = getTotalPasses(row);
                                return (
                                    <div
                                        key={row.deviceId}
                                        className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-xl overflow-hidden shadow-sm transition-all duration-200"
                                    >
                                        {/* Main Row Content */}
                                        <div
                                            className="grid px-6 py-4 items-center cursor-pointer hover:bg-[var(--hover-bg)] gap-4"
                                            style={gridStyle}
                                            onClick={() => toggleRow(row.deviceId)}
                                        >
                                            <div className="flex items-center gap-3 overflow-hidden">
                                                <span className={`material-icons-outlined text-[var(--text-tertiary)] transition-transform duration-200 text-[20px] flex-shrink-0 ${expandedRows[row.deviceId] ? 'rotate-90' : ''}`}>
                                                    keyboard_arrow_right
                                                </span>
                                                <div className="min-w-0">
                                                    <div className="font-bold text-[var(--text-primary)] text-sm truncate">{row.deviceName || 'Unknown Device'}</div>
                                                    <div className="text-xs text-[var(--text-secondary)] truncate">ID: #{row.deviceId}</div>
                                                </div>
                                            </div>

                                            {selectedGeofences.map(geofence => {
                                                const count = row.counts[geofence.value] || 0;
                                                const isHigh = count > highlightThreshold;
                                                return (
                                                    <div key={geofence.value} className="text-center">
                                                        <span className={`px-2.5 py-1 rounded text-sm font-semibold inline-block ${isHigh ? 'bg-red-100 text-red-600 border border-red-100 dark:bg-red-900/40 dark:text-red-400 dark:border-red-800' : 'bg-[var(--bg-tertiary)] text-[var(--text-secondary)]'}`}>
                                                            {count}
                                                        </span>
                                                    </div>
                                                );
                                            })}

                                            <div className="text-right font-bold text-blue-600 pr-2">
                                                {totalPasses}
                                            </div>
                                        </div>

                                        {/* Expanded Details */}
                                        {expandedRows[row.deviceId] && (
                                            <div className="border-t border-[var(--border-color)] bg-[var(--bg-tertiary)]/30 p-4 animate-fadeIn">
                                                <div className="mb-3 px-2 flex justify-between items-center">
                                                    <h4 className="text-xs font-bold text-[var(--text-secondary)] uppercase tracking-widest">Event Logs</h4>
                                                </div>
                                                <div className="overflow-x-auto">
                                                    <table className="w-full text-sm">
                                                        <thead className="text-xs text-[var(--text-tertiary)] border-b border-[var(--border-color)]">
                                                            <tr>
                                                                <th className="text-left py-2 font-medium px-2">TIME</th>
                                                                <th className="text-left py-2 font-medium">GEOFENCE</th>
                                                                <th className="text-left py-2 font-medium">TYPE</th>
                                                                <th className="text-right py-2 font-medium px-2">POSITION ID</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody className="divide-y divide-[var(--border-color)]">
                                                            {row.events.sort((a, b) => new Date(a.createdOn) - new Date(b.createdOn)).map((event, idx) => (
                                                                <tr key={idx} className="hover:bg-[var(--hover-bg)] transition-colors text-[var(--text-primary)]">
                                                                    <td className="py-3 px-2 text-[var(--text-secondary)] font-mono text-xs">{event.createdOn}</td>
                                                                    <td className="py-3 font-medium text-[var(--text-primary)]">{event.geofenceName || '-'}</td>
                                                                    <td className="py-3">
                                                                        {event.eventType === 'geofenceEnter' ? (
                                                                            <span className="bg-emerald-50 text-emerald-600 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-tighter border border-emerald-100 inline-flex items-center gap-1 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-800">
                                                                                Enter
                                                                            </span>
                                                                        ) : (
                                                                            <span className="bg-red-50 text-red-600 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-tighter border border-red-100 inline-flex items-center gap-1 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800">
                                                                                Exit
                                                                            </span>
                                                                        )}
                                                                    </td>
                                                                    <td className="py-3 px-2 text-right text-[var(--text-tertiary)] font-mono text-xs">#{event.positionId}</td>
                                                                </tr>
                                                            ))}
                                                            {row.events.length === 0 && (
                                                                <tr>
                                                                    <td colSpan="4" className="py-4 text-center text-[var(--text-tertiary)] italic">No detailed events recorded</td>
                                                                </tr>
                                                            )}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })
                        ) : (
                            <div className="text-center py-12 bg-[var(--bg-secondary)] rounded-xl border border-[var(--border-color)] text-[var(--text-tertiary)]">
                                <span className="material-icons-outlined text-4xl mb-2">search_off</span>
                                <p>No devices found matching your criteria</p>
                            </div>
                        )}
                    </div>

                    {/* Footer Pagination (Mock) */}
                    <div className="flex items-center justify-between px-2 pt-4 mt-4">
                        <div className="text-sm text-[var(--text-secondary)]">
                            Showing <span className="font-semibold text-[var(--text-primary)]">{filteredRows.length}</span> results
                        </div>
                    </div>
                </>
            )}

            <style>{`
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(-4px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                .animate-fadeIn {
                    animation: fadeIn 0.2s ease-out forwards;
                }
            `}</style>
        </div>
    );
};

export default RecceReport;
